import React from 'react';
import PropTypes from 'prop-types';
import { Panel } from 'react-bootstrap';
import GroupByCodeTree from './GroupByCodeTree.jsx';
import ActivityGroup from './ActivityGroup.jsx';
import Utils from '../util/Utils.jsx';

export default class GroupTree extends React.Component {
	constructor(props) {
		super(props);

		this.getGroupsByArea = this.getGroupsByArea.bind(this);
		this.getGroupsByDate = this.getGroupsByDate.bind(this);
		this.getGroupsByFloat = this.getGroupsByFloat.bind(this);
		this.getGroupsByResource = this.getGroupsByResource.bind(this);
	}

	// WARNING: Check this function if the component is not updating when you need it to
	shouldComponentUpdate(nextProps, nextState) {
		return this.props.groupBy !== nextProps.groupBy ||
			this.props.isBulkActive !== nextProps.isBulkActive ||
			JSON.stringify(this.props.bulkActivities) !== JSON.stringify(nextProps.bulkActivities) ||
			this.props.groupByActivityCode !== nextProps.groupByActivityCode ||
			JSON.stringify(this.props.filteredActivitiesMap) !== JSON.stringify(nextProps.filteredActivitiesMap) ||
			JSON.stringify(this.props.filteredActivities) !== JSON.stringify(nextProps.filteredActivities);
	}

	getGroupsByArea() {
		const { filteredActivities } = this.props;
		const groups = {};
		for (let i = 0; i < filteredActivities.length; i++) {
			const activity = filteredActivities[i];
			activity.area = activity.area || 'Unknown Area';
			const areaName = Utils.makeSafeForJS(activity.area);
			if (!(areaName in groups)) {
				groups[areaName] = {name: areaName, textLabel: activity.area, activityIds: []};
			}
			groups[areaName].activityIds.push(activity.activityId);
		}
		return groups;
	}

	getGroupsByDate() {
		const { filteredActivities } = this.props;

		const groups = {
			beforeLastWeek: {textLabel: 'Before Last Week', activityIds: []},
			lastWeek: {textLabel: 'Last Week', activityIds: []},
			thisWeek: {textLabel: 'This Week', activityIds: []},
			nextWeek: {textLabel: 'Next Week', activityIds: []},
			afterNextWeek: {textLabel: 'After Next Week', activityIds: []}
		};

		let start;

		let d = new Date();
		d.setHours(0, 0, 0, 0);
		const lastWeek = [start = new Date(d.setDate(d.getDate() - d.getDay() - 7)), new Date(d.setDate(start.getDate() + 7))];

		d = new Date();
		d.setHours(0, 0, 0, 0);
		const thisWeek = [start = new Date(d.setDate(d.getDate() - d.getDay())), new Date(d.setDate(start.getDate() + 7))];

		d = new Date();
		d.setHours(0, 0, 0, 0);
		const nextWeek = [start = new Date(d.setDate(d.getDate() - d.getDay() + 7)), new Date(d.setDate(start.getDate() + 7))];

		for (let i = 0; i < filteredActivities.length; i++) {
			const activity = filteredActivities[i];

			if (activity.start < lastWeek[0].getTime()) {
				groups.beforeLastWeek.activityIds.push(activity.activityId);
			}
			if ((activity.start <= lastWeek[1].getTime()) && (activity.finish >= lastWeek[0].getTime())) {
				groups.lastWeek.activityIds.push(activity.activityId);
			}
			if ((activity.start <= thisWeek[1].getTime()) && (activity.finish >= thisWeek[0].getTime())) {
				groups.thisWeek.activityIds.push(activity.activityId);
			}
			if ((activity.start <= nextWeek[1].getTime()) && (activity.finish >= nextWeek[0].getTime())) {
				groups.nextWeek.activityIds.push(activity.activityId);
			}
			if (activity.finish > nextWeek[1].getTime()) {
				groups.afterNextWeek.activityIds.push(activity.activityId);
			}
		}
		return groups;
	}

	getGroupsByFloat() {
		const { filteredActivities } = this.props;
		const groups = {
			lessThan0d: {textLabel: 'Less Than 0d', activityIds: []},
			'0To10d': {textLabel: '0d to 10d', activityIds: []},
			greaterThan10d: {textLabel: 'Greater Than 10d', activityIds: []}
		};
		for (let i = 0; i < filteredActivities.length; i++) {
			const activity = filteredActivities[i];
			if (activity.totalFloat < 0) {
				groups.lessThan0d.activityIds.push(activity.activityId);
			} else if (activity.totalFloat > 10) {
				groups.greaterThan10d.activityIds.push(activity.activityId);
			} else {
				groups['0To10d'].activityIds.push(activity.activityId);
			}
		}
		return groups;
	}

	getGroupsByResource() {
		const { filteredActivities, datasetInfo } = this.props;
		const groups = {};

		const resourceList = datasetInfo && datasetInfo.filterOptions && datasetInfo.filterOptions.resources
			? datasetInfo.filterOptions.resources
			: [];
		for (const r in resourceList) {
			const resource = resourceList[r];
			groups[r] = {textLabel: resource, activityIds: []};

			for (let i = 0; i < filteredActivities.length; i++) {
				const activity = filteredActivities[i];
				const resourceMap = activity && activity.resourcesHashMap;
				if (resourceMap && resource in resourceMap) {
					groups[r].activityIds.push(activity.activityId);
				}
			}
		}
		return groups;
	}

	render() {
		const { filteredActivities, filteredActivitiesMap, activityCodesTree, datasetInfo, groupBy, groupByActivityCode } = this.props;
		if (!filteredActivities || !datasetInfo) {
			return (<div>Please select a project to view the Group List</div>);
		}

		if (!groupBy) {
			return (<div>Please select a group by method</div>);
		}

		if (!filteredActivities.length) {
			return <div>All of your activities have been filtered.</div>;
		}

		if (groupBy === 'Activity Code') {
			if (!groupByActivityCode) {
				return (<div>Please select an activity code to group by</div>);
			}
			const groupNode = activityCodesTree && activityCodesTree[groupByActivityCode];
			if (!groupNode || !Array.isArray(groupNode.childrenSequence)) {
				return null;
			}
			return (
				<GroupByCodeTree
					filteredActivities={filteredActivities}
					filteredActivitiesMap={filteredActivitiesMap}
					activityCodesTree={groupNode}
					datasetInfo={datasetInfo} />
			);
		} else {
			const groups = ((groupBy) => {
				switch (groupBy) {
					case 'Area':
						return this.getGroupsByArea();
					case 'Date':
						return this.getGroupsByDate();
					case 'Float':
						return this.getGroupsByFloat();
					case 'Resource':
						return this.getGroupsByResource();
					default:
						return {};
				}
			})(groupBy);

			if (!Object.keys(groups).length) {
				return (<div>No activities match your filters.</div>);
			}
			const panels = [];
			for (const g in groups) {
				const group = groups[g];
				if (group.activityIds.length) {
					const activitiesForGroup = group.activityIds.filter(activityId => {
						return activityId in filteredActivitiesMap && filteredActivitiesMap[activityId] in filteredActivities;
					}).map(activityId => {
						return filteredActivities[filteredActivitiesMap[activityId]];
					});

					panels.push(
						<Panel
							defaultExpanded
							className="groupTreePanel swaTree"
							key={g}>
							<Panel.Heading>
								<Panel.Title toggle>{`${group.textLabel} (${group.activityIds.length})`}</Panel.Title>
							</Panel.Heading>
							<Panel.Collapse>
								<ActivityGroup
									className="groupTreeActivityGroup"
									activities={activitiesForGroup}
									datasetInfo={datasetInfo} />
							</Panel.Collapse>
						</Panel>
					);
				}
			}

			const codeNames = datasetInfo && datasetInfo.filterOptions && datasetInfo.filterOptions.activityCodesHashMap;
			const headerExtra = codeNames && groupByActivityCode && groupByActivityCode in codeNames ? ` ${codeNames[groupByActivityCode]}` : '';
			return (
				<Panel
					defaultExpanded
					className="groupTreePanel swaTree">
					<Panel.Heading>
						<Panel.Title toggle>{`Group List by ${groupBy}${headerExtra}`}</Panel.Title>
					</Panel.Heading>
					<Panel.Collapse>
						<Panel.Body>
							{panels}
						</Panel.Body>
					</Panel.Collapse>
				</Panel>
			);
		}
	}
}

GroupTree.propTypes = {
	filteredActivities: PropTypes.array,
	filteredActivitiesMap: PropTypes.object,
	activityCodesTree: PropTypes.object,
	groupBy: PropTypes.string,
	groupByActivityCode: PropTypes.string,
	datasetInfo: PropTypes.object,
	isBulkActive: PropTypes.bool,
	bulkActivities: PropTypes.array
};